import { useSketchStore } from '../../state/useSketchStore';
import { useDocumentStore } from '../../state/useDocumentStore';
import { identityPlacement } from '../scene';
import type { SceneObject } from '../scene';
import type { SketchDimension, SketchLabel, SketchSolid, SketchTag, SketchLooseEdge } from './types';
import { solidEffectiveVisible } from './types';

/**
 * Sketch → scene ledger bridge.
 *
 * The cross-workbench sceneObjects ledger (Phase A2) is the shared picking /
 * visibility source of truth; the Sketch store keeps owning the rich model.
 * This bridge replaces the ledger's 'sketch' records wholesale whenever the
 * solids, loose edges, dimensions or labels maps change — tools, undo/redo,
 * hydrate all flow through the same single diff point (same design family as
 * startToolSyncBridge). Loose edges mirror as 'line' citizens so a line
 * drawn on a sketch face/ground is a live object in every workbench (B4);
 * dimensions and labels mirror as annotation citizens for the same reason.
 */

let started = false;

export function startSketchLedgerSync(): void {
    if (started) return;
    started = true;

    const toSceneObject = (solid: SketchSolid, tags: Record<string, SketchTag>): SceneObject => ({
        id: solid.id,
        source: 'sketch',
        kind: 'mesh',
        displayName: solid.name,
        layerId: 'sketch',
        visible: solidEffectiveVisible(solid, tags),
        locked: solid.locked,
        placement: identityPlacement(),
        data: { sketch: solid },
    });

    const edgeToSceneObject = (edge: SketchLooseEdge): SceneObject => ({
        id: edge.id,
        source: 'sketch',
        kind: 'line',
        displayName: 'Sketch Edge',
        layerId: 'sketch',
        visible: true,
        locked: false,
        placement: identityPlacement(),
        data: { sketchEdge: { a: edge.a, b: edge.b } },
    });

    // Annotations join the ledger too: dimensions and text labels drawn at
    // the Sketch workbench are live, selectable citizens in every other
    // workbench (edits route back through the edit router).
    const dimensionToSceneObject = (dimension: SketchDimension): SceneObject => ({
        id: dimension.id,
        source: 'sketch',
        kind: 'dimension',
        displayName: dimension.text ?? dimension.value.toFixed(2),
        layerId: 'sketch',
        visible: true,
        locked: false,
        placement: identityPlacement(),
        data: { sketchDimension: { ...dimension } },
    });

    const labelToSceneObject = (label: SketchLabel): SceneObject => ({
        id: label.id,
        source: 'sketch',
        kind: 'text',
        displayName: label.text,
        layerId: 'sketch',
        visible: true,
        locked: false,
        placement: identityPlacement(),
        data: { sketchLabel: { ...label } },
    });

    let prev = useSketchStore.getState().solids;
    let prevTags = useSketchStore.getState().tags;
    let prevEdges = useSketchStore.getState().edges;
    let prevDimensions = useSketchStore.getState().dimensions;
    let prevLabels = useSketchStore.getState().labels;

    const sync = (state: ReturnType<typeof useSketchStore.getState>) => {
        useDocumentStore
            .getState()
            .replaceSceneLedgerSource('sketch', [
                ...Object.values(state.solids).map((s) => toSceneObject(s, state.tags)),
                ...Object.values(state.edges).map(edgeToSceneObject),
                ...Object.values(state.dimensions).map(dimensionToSceneObject),
                ...Object.values(state.labels).map(labelToSceneObject),
            ]);
    };

    useSketchStore.subscribe((state) => {
        if (
            state.solids === prev && state.tags === prevTags && state.edges === prevEdges
            && state.dimensions === prevDimensions && state.labels === prevLabels
        ) return;
        prev = state.solids;
        prevTags = state.tags;
        prevEdges = state.edges;
        prevDimensions = state.dimensions;
        prevLabels = state.labels;
        sync(state);
    });
}
